import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { resolveImage } from "../lib/image";
import { fmt } from "../lib/money";

const STEPS = ["placed", "shipped", "delivered"];

export default function OrderDetail() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    api.get(`/orders/${id}`).then((r) => setOrder(r.data)).catch(() => {});
  }, [id]);

  if (!order) return <div className="p-20 text-center">Loading…</div>;

  const stepIndex = order.status === "cancelled" ? -1 : STEPS.indexOf(order.status);

  return (
    <div className="max-w-[1000px] mx-auto px-6 lg:px-10 py-10">
      <Link to="/orders" className="text-xs uppercase tracking-widest text-neutral-500 hover:text-black">← All orders</Link>
      <h1 className="font-display font-black text-4xl tracking-tight mt-4">Order #{order.order_id.slice(-8)}</h1>
      <div className="text-sm text-neutral-600 mt-1">Placed {new Date(order.created_at).toLocaleString()}</div>

      {/* Tracker */}
      {order.status !== "cancelled" && (
        <div className="mt-10 grid grid-cols-3 border border-neutral-200">
          {STEPS.map((s, i) => (
            <div
              key={s}
              data-testid={`tracker-${s}`}
              className={`p-5 border-r border-neutral-200 last:border-r-0 ${i <= stepIndex ? "bg-black text-white" : ""}`}
            >
              <div className="text-[10px] uppercase tracking-widest opacity-70">Step {i + 1}</div>
              <div className="font-display font-bold text-lg capitalize mt-1">{s}</div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-10 grid lg:grid-cols-[1fr_320px] gap-10">
        <div className="border-t border-neutral-200">
          {order.items.map((it, idx) => (
            <div key={`${it.product_id}-${idx}`} className="flex gap-5 py-5 border-b border-neutral-200">
              <div className="w-20 h-20 bg-neutral-100 border border-neutral-200 overflow-hidden">
                {it.image && <img src={resolveImage(it.image)} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1">
                <Link to={`/products/${it.product_id}`} className="font-display font-bold hover:text-[#0033FF]">{it.title}</Link>
                {it.seller_id && it.seller_name && (
                  <Link
                    to={`/shop/${it.seller_id}`}
                    className="block text-[10px] uppercase tracking-widest mt-0.5 text-[#FF2A00] font-semibold hover:underline"
                  >
                    Sold by <span className="italic">{it.seller_name}</span> →
                  </Link>
                )}
                <div className="text-sm text-neutral-500 mt-1">Qty {it.quantity} · {fmt(it.price)}</div>
                {(it.size || it.color) && (
                  <div className="flex gap-2 mt-1 text-[11px] uppercase tracking-wider text-neutral-600">
                    {it.size && <span>Size: {it.size}</span>}
                    {it.color && <span>Color: {it.color}</span>}
                  </div>
                )}
              </div>
              <div className="font-display font-black">{fmt(it.price * it.quantity)}</div>
            </div>
          ))}
        </div>

        <aside className="space-y-6">
          <div className="border border-neutral-200 p-5">
            <h3 className="font-display font-bold uppercase text-xs tracking-wider mb-3">Shipping</h3>
            <div className="text-sm leading-relaxed">
              <div className="font-semibold">{order.shipping_address.full_name}</div>
              <div>{order.shipping_address.line1}</div>
              {order.shipping_address.line2 && <div>{order.shipping_address.line2}</div>}
              <div>{order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.postal_code}</div>
              <div>{order.shipping_address.country}</div>
              <div className="mt-1 text-neutral-600">{order.shipping_address.phone}</div>
            </div>
          </div>
          <div className="border border-neutral-200 p-5">
            <h3 className="font-display font-bold uppercase text-xs tracking-wider mb-3">Summary</h3>
            <div className="text-sm space-y-2">
              <div className="flex justify-between"><span>Subtotal</span><span>{fmt(order.subtotal)}</span></div>
              {Array.isArray(order.discount_breakdown) && order.discount_breakdown.length > 0 && (
                <div className="pt-2 border-t border-neutral-300 space-y-1">
                  {order.discount_breakdown.map((d) => (
                    <div key={d.code} className="flex justify-between text-[#00A355]">
                      <span className="truncate"><code className="font-bold">{d.code}</code> · {d.seller_name}</span>
                      <span className="font-semibold">
                        {d.discount_type === "free_shipping"
                          ? "Free shipping"
                          : d.discount_type === "shipping_percent"
                          ? `−${fmt(d.amount)} shipping`
                          : `− ${fmt(d.amount)}`}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex justify-between"><span>Shipping</span><span>{order.shipping === 0 ? "Free" : fmt(order.shipping)}</span></div>
              <div className="flex justify-between pt-2 border-t border-neutral-300 font-bold text-base">
                <span>Total</span><span data-testid="order-total">{fmt(order.total)}</span>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
